import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toBlob } from 'html-to-image';
import { BaseComponent } from 'src/app/core/base/base.component';
import { CommonPatientViewModel } from 'src/app/model/common/patient.view';
import { CommonTurnViewModel } from 'src/app/model/common/turn.view';
import { OdontoDiagnosticModel } from 'src/app/model/odonto/diagnostic';
import { OdontoDiagnosticPlanModel } from 'src/app/model/odonto/diagnostic-plan';
import { OdontoHistoryModel } from 'src/app/model/odonto/history';
import { OdontoOdontogramViewModel } from 'src/app/model/odonto/odontogram.view';
import { OdontoStomatognathicModel } from 'src/app/model/odonto/stomatognathic';
import { OdontoWrapperDto } from 'src/app/model/odonto/wrapper.dto';
import { ODONTO_MODULE_CONSTANTS } from './odonto.constants';
import { OdontoMainService } from './odonto.service';

@Component({
  selector: 'ms-odonto-main',
  templateUrl: './odonto.component.html',
})
export class OdontoMainComponent extends BaseComponent {
  readonly screen = ODONTO_MODULE_CONSTANTS;
  turnId = -1;
  turn: CommonTurnViewModel = {} as CommonTurnViewModel;
  oldTurns: CommonTurnViewModel[] = [];
  odontoWrapper: OdontoWrapperDto = {} as OdontoWrapperDto;
  turnStatusConsult?: string;
  turnStatusFinish?: string;
  patient: CommonPatientViewModel = {};
  saving = false;
  isEdit = false;
  isSticky = false;
  isFirst = true;

  constructor(
    protected route: ActivatedRoute,
    protected service: OdontoMainService,
    protected router: Router
  ) {
    super();
  }

  onInit = () => {
    this.service.setScreenInformation({
      id: this.screen.id,
      title: this.screen.title,
      showHeading: false,
    });
    this.subscriptions.push(
      this.route.queryParams.subscribe((params) => {
        this.turnId = params.turn;
        this.isEdit = params.isEdit;
        this.getTurnInformation();
      })
    );
  };

  save = ({ draft, isReport }: { draft?: boolean; isReport?: boolean }) => {
    this.saving = true;
    this.service.setLoadingState();
    this.setWrapperBeforeSave();
    this.subscriptions.push(
      this.service.save(this.odontoWrapper, this.turn.patient).subscribe(() => {
        this.getTurnInformation();
      })
    );

    const odontogram = document.getElementById('odontogram-view');
    const oralHealth = document.getElementById('oral-health-view');
    if (odontogram && oralHealth) {
      Promise.all([
        toBlob(odontogram, { quality: 1 }),
        toBlob(oralHealth, { quality: 1 }),
      ]).then((results) => {
        const odontogramImage = new File([results[0]!], 'odontogram.png', {
          type: 'image/png',
        });
        const oralHealthImage = new File([results[1]!], 'oral-health.png', {
          type: 'image/png',
        });
        this.subscriptions.push(
          this.service
            .putFiles(
              odontogramImage,
              oralHealthImage,
              this.turn.patientClinicHistory
            )
            .subscribe(() => {
              if (!draft) {
                this.subscriptions.push(
                  this.service
                    .moveTurnStatus(this.turn.id, this.turnStatusFinish)
                    .subscribe(() => this.router.navigate(['/turno']))
                );
              }
              if (isReport) {
                this.subscriptions.push(
                  this.service
                    .generateCertificate(
                      this.turn.patientClinicHistory,
                      this.turn.id
                    )
                    .subscribe(() => (this.saving = false))
                );
              } else {
                this.saving = false;
              }
            })
        );
      });
    }
  };

  private getTurnInformation = () => {
    this.subscriptions.push(
      this.service.getTurn(this.turnId).subscribe((turn) => {
        this.turn = {
          ...turn,
          turnDate: new Date(this.turn.turnDate!),
        };
        this.pushAllSubscription();
        if (!this.isEdit) {
          this.subscriptions.push(
            this.service
              .getOldTurns(this.turn.patient!, this.turn.id!)
              .subscribe((turns) => {
                this.isFirst =
                  turns.filter(
                    (turn) => turn.specialtyCodeAlt?.toLowerCase() === 'odon'
                  ).length === 0;
                this.oldTurns = turns.filter(
                  (turn) => turn.specialtyCodeAlt?.toLowerCase() !== 'odon'
                );
              })
          );
        }
      })
    );
  };

  private pushAllSubscription = () => {
    this.subscriptions.push(
      this.service
        .getPatientView(this.turn.patient!)
        .subscribe((patient) => (this.patient = patient)),
      this.service.getPatientById(this.turn.patient!).subscribe(),
      this.service.getPersonById(this.turn.person!).subscribe(),
      this.service.readTurnStatusStore().subscribe((turnStatus) => {
        if (turnStatus.length > 0) {
          const consult = turnStatus.find(
            (status) => status.codeAlt === 'in-progress'
          )!;
          this.turnStatusConsult = consult.id;
          const finish = turnStatus.find(
            (status) => status.codeAlt === 'finish'
          )!;
          this.turnStatusFinish = finish.id;

          if (this.turn.turnStatusCodeAlt?.toLowerCase() === 'waiting') {
            this.subscriptions.push(
              this.service
                .moveTurnStatus(this.turn.id!, this.turnStatusConsult!)
                .subscribe()
            );
          }
        }
      }),
      this.service
        .getOdontologyInformation(this.turn.patient, this.turn.id, this.turn.specialty)
        .subscribe((wrapper) => {
          this.odontoWrapper = wrapper;
          this.service
            .getOralHygiene(this.odontoWrapper.oralHealth?.id)
            .subscribe(
              (oralHygiene) =>
                (this.odontoWrapper!.oralHygieneView = oralHygiene)
            );
        })
    );
  };

  private setWrapperBeforeSave = () => {
    this.odontoWrapper.stomatognathic = this.odontoWrapper
      .stomatognathicView!.filter((item) => item.stomatognathic || item.details)
      .map(
        (item) =>
          ({
            id: item.stomatognathic,
            patient: item.patient,
            stomatognathic: item.id,
            details: item.details,
          } as OdontoStomatognathicModel)
      );

    this.odontoWrapper.history = this.odontoWrapper
      .historyView!.filter((item) => item.history || item.details)
      .map(
        (item) =>
          ({
            id: item.history,
            patient: item.patient,
            history: item.id,
            details: item.details,
          } as OdontoHistoryModel)
      );

    this.odontoWrapper.odontogram = [];
    this.odontoWrapper.odontogramView?.forEach(this.setOdontogramBeforeSave);

    this.odontoWrapper.oralHygiene = this.odontoWrapper
      .oralHygieneView!.filter((item) => item.changed)
      .map((item) => ({
        id: item.id,
        oralHealth: item.oralHealth,
        partGroup: item.partGroup,
        partNumber01: item.partNumber01,
        partNumber02: item.partNumber02,
        partNumber03: item.partNumber03,
        plaque: item.plaque,
        calc: item.calc,
        gingivitis: item.gingivitis,
      }));

    this.odontoWrapper.diagnosticPlan = this.odontoWrapper
      .diagnosticPlanView!.filter((item) => item.details || item.filled)
      .map(
        (item) =>
          ({
            id: item.diagnosticPlan,
            diagnosticPlan: item.id,
            details: item.details,
            patient: item.patient,
          } as OdontoDiagnosticPlanModel)
      );

    this.odontoWrapper.diagnostic = this.odontoWrapper
      .diagnosticView!.filter((item) => item.description)
      .map(
        (item) =>
          ({
            id: item.id,
            cie: item.cie,
            description: item.description,
            isFinal: item.isFinal,
            patient: item.patient,
          } as OdontoDiagnosticModel)
      );
  };

  private setOdontogramBeforeSave = (item: OdontoOdontogramViewModel) => {
    if (item.children && item.children.length > 0) {
      item.children.forEach(this.setOdontogramBeforeSave);
    }
    if (item.changed) {
      const { odontogram, id, patient, recession, mobility, symbology, color } =
        item;
      this.odontoWrapper!.odontogram?.push({
        id: odontogram,
        patient,
        teeth: id,
        recession,
        mobility,
        symbology,
        color,
      });
    }
  };
}
